// S11 Help now (Tool Plan §3 rule 6, §4.15, §12, D-69; I-1a batch 5, IP2-32 + IP2-33).
//
// One tap from every screen, before and after unlock, offline: everything here
// is bundled into the shell, no request is made. Layout (wireframe v11 S11):
//   the pinned strip, split in two — "Danger or medical emergency" (MDA · Police)
//   and "Need to talk now" (ERAN, which is not an emergency service) —
//   then the help-line cards, each with its Call / Message / chat buttons (>= 48 px).
// "My people" (encrypted like the diary, shown after unlock) and the medical
// panel are I-1b. Every call, message or chat starts only on the person's tap:
// they are plain links. All text is rendered as text nodes, never as HTML.
// The tap itself puts the app-switcher cover up (`onHandoff`): the dialer is
// opened, and its snapshot of this screen taken, before `blur` fires.

import { common, safety } from '../screens/copy'
import { ScreenTitle } from '../ui/ScreenTitle'

import { type HelpItem, layoutHelp, smsHref, telHref } from './help'
import { HELP_PACKAGE } from './helpPackage.generated'
import { coverForHandoff } from './useAppSwitcherCover'

interface HelpNowProps {
  readonly onBack: () => void
}

/** On `click`, not `pointerdown`: a scroll that starts on a button never flashes the cover. */
function onHandoff(): void {
  coverForHandoff()
}

function ChatLink({ item }: { readonly item: HelpItem }): React.JSX.Element | null {
  if (!item.chatUrl || !item.chatKind) {
    return null
  }
  // A new browsing context, with no opener and no referrer: the line's page or
  // WhatsApp learns nothing about this app.
  return (
    <a className="button help__button" href={item.chatUrl} target="_blank" rel="noopener noreferrer" onClick={onHandoff}>
      {safety.chat[item.chatKind]}
    </a>
  )
}

function HelpCard({ item }: { readonly item: HelpItem }): React.JSX.Element {
  const hours = safety.hours(item)
  return (
    <li className="help__card">
      <p className="help__card-title">{item.title ?? item.label}</p>
      {item.tel ? (
        <p className="help__number" dir="ltr">
          {item.tel}
        </p>
      ) : null}
      {hours ? <p className="help__hours">{hours}</p> : null}
      {item.note ? <p className="help__hours">{item.note}</p> : null}
      <div className="help__row">
        {item.tel ? (
          <a className="button help__button" href={telHref(item.tel)} onClick={onHandoff}>
            {safety.call(item.tel)}
          </a>
        ) : null}
        {item.sms ? (
          <a className="button help__button" href={smsHref(item.sms)} onClick={onHandoff}>
            {safety.message}
          </a>
        ) : null}
        <ChatLink item={item} />
      </div>
    </li>
  )
}

export function HelpNow({ onBack }: HelpNowProps): React.JSX.Element {
  const { danger, talk, lines } = layoutHelp(HELP_PACKAGE.items)
  return (
    <section className="screen help">
      <div className="screen__body">
        <ScreenTitle>{safety.helpNow}</ScreenTitle>
        {danger.length + talk.length > 0 ? (
          <div className="help__strip">
            {danger.length > 0 ? (
              <>
                <p className="help__strip-title">{safety.dangerTitle}</p>
                <div className="help__row">
                  {danger.map((item) => (
                    <a
                      key={item.id}
                      className="button button--primary help__button"
                      href={telHref(item.tel ?? '')}
                      onClick={onHandoff}
                    >
                      {safety.callNamed(item.label, item.tel ?? '')}
                    </a>
                  ))}
                </div>
              </>
            ) : null}
            {talk.length > 0 ? (
              <>
                <p className="help__strip-title">{safety.talkTitle}</p>
                <div className="help__row">
                  {talk.map((item) => (
                    <a
                      key={item.id}
                      className="button help__button"
                      href={telHref(item.tel ?? '')}
                      onClick={onHandoff}
                    >
                      {safety.callNamed(item.label, item.tel ?? '')}
                    </a>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        ) : null}
        <p className="screen__text">{safety.notEmergency}</p>
        {lines.length > 0 ? (
          <>
            <h2 className="help__heading">{safety.linesTitle}</h2>
            <ul className="help__lines">
              {lines.map((item) => (
                <HelpCard key={item.id} item={item} />
              ))}
            </ul>
          </>
        ) : null}
        <div className="actions">
          <button type="button" className="button button--quiet" onClick={onBack}>
            {common.back}
          </button>
        </div>
      </div>
    </section>
  )
}
